import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useFarmFromUrl } from '@/shared/hooks/useUrlSync';
import { useFarmStore } from '@/features/farms/stores/farmStore';
import { farmsApi } from '@/features/farms/api/farmsApi';
import { queryKeys } from '@/shared/api/queryClient';
import { buildRoute } from '@/shared/lib/constants';
import { ChevronDown, Building2, ExternalLink } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

export const FarmSelector: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { farmId, farm: activeFarm, isLoading: isFarmLoading } = useFarmFromUrl();
  const { selectedFarm } = useFarmStore();

  // Fetch all farms the user has access to
  const { data: farms = [], isLoading } = useQuery({
    queryKey: queryKeys.farms.all(),
    queryFn: () => farmsApi.list(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFarmSelect = (farmId: string) => {
    navigate(buildRoute.farm(farmId));
    setIsOpen(false);
  };

  const handleViewAllFarms = () => {
    navigate(buildRoute.farms());
    setIsOpen(false);
  };

  // Determine which farm to display
  // Priority: activeFarm from URL > selectedFarm from store > null
  const displayFarm = activeFarm || selectedFarm;

  // If farmId exists in URL but farm is loading, show loading skeleton
  if (farmId && isFarmLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white border border-slate-200/50">
        <div className="h-7 w-7 rounded-lg bg-slate-200 animate-pulse"></div>
        <div className="hidden sm:block space-y-1.5">
          <div className="h-3 w-24 bg-slate-200 rounded animate-pulse"></div>
          <div className="h-2 w-16 bg-slate-100 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  // If no farm is available at all, show "Select your farm" button
  if (!displayFarm) {
    return (
      <button
        onClick={handleViewAllFarms}
        className={cn(
          "flex items-center gap-2 px-4 py-2 rounded-xl",
          "bg-gradient-to-r from-emerald-50 to-teal-50",
          "border border-emerald-200/50",
          "text-emerald-700 hover:text-emerald-800",
          "transition-all duration-200",
          "hover:shadow-md hover:shadow-emerald-500/10",
          "focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
        )}
      >
        <Building2 className="h-4 w-4" />
        <span className="text-sm font-medium">Select your farm</span>
      </button>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Active Farm Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-3 px-4 py-2 rounded-xl",
          "bg-white border border-slate-200/50",
          "text-slate-700 hover:text-slate-900",
          "transition-all duration-200",
          "hover:border-emerald-300 hover:shadow-sm",
          "focus:outline-none focus:ring-2 focus:ring-emerald-500/20",
          isOpen && "border-emerald-300 shadow-sm ring-2 ring-emerald-500/20"
        )}
      >
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-xs font-semibold text-white shadow-sm">
            {displayFarm.name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div className="text-left hidden sm:block">
            <div className="text-sm font-medium text-slate-900 tracking-tight">
              {displayFarm.name || 'Unnamed Farm'}
            </div>
            {displayFarm.location && (
              <div className="text-xs text-slate-500">
                {displayFarm.location}
              </div>
            )}
          </div>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-slate-400 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-80 origin-top-left animate-in z-50">
          <div className="rounded-xl bg-white shadow-xl border border-slate-200/50 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-200/50">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Your Farms
              </div>
            </div>

            {/* Farm List */}
            <div className="max-h-80 overflow-y-auto py-1.5">
              {isLoading ? (
                <div className="px-4 py-8 text-center">
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-emerald-500 border-r-transparent"></div>
                  <div className="mt-2 text-sm text-slate-500">Loading farms...</div>
                </div>
              ) : farms.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-600">No farms found</div>
                  <div className="text-xs text-slate-500 mt-1">Create your first farm to get started</div>
                </div>
              ) : (
                farms.map((farm) => {
                  const isActive = displayFarm?.id === farm.id;

                  return (
                    <button
                      key={farm.id}
                      onClick={() => handleFarmSelect(farm.id)}
                      className={cn(
                        "flex w-full items-center gap-3 px-4 py-3 text-left transition-all duration-200",
                        isActive
                          ? "bg-emerald-50 text-emerald-700"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      {/* Farm Avatar */}
                      <div
                        className={cn(
                          "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-sm font-semibold shadow-sm",
                          isActive
                            ? "bg-gradient-to-br from-emerald-500 to-teal-600 text-white"
                            : "bg-gradient-to-br from-slate-200 to-slate-300 text-slate-700"
                        )}
                      >
                        {farm.name?.charAt(0).toUpperCase() || '?'}
                      </div>

                      {/* Farm Info */}
                      <div className="flex-1 min-w-0">
                        <div className={cn(
                          "text-sm font-medium truncate",
                          isActive ? "text-emerald-900" : "text-slate-900"
                        )}>
                          {farm.name || 'Unnamed Farm'}
                        </div>
                        {farm.location && (
                          <div className={cn(
                            "text-xs truncate mt-0.5",
                            isActive ? "text-emerald-600" : "text-slate-500"
                          )}>
                            {farm.location}
                          </div>
                        )}
                      </div>

                      {/* Active Indicator */}
                      {isActive && (
                        <div className="flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/50"></div>
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer - View All Farms Link */}
            <div className="border-t border-slate-200/50 p-1.5">
              <button
                onClick={handleViewAllFarms}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm text-slate-700 transition-all duration-200 hover:bg-slate-50 group"
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 group-hover:bg-emerald-100 group-hover:text-emerald-600 transition-colors duration-200">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <span className="font-medium">View all farms</span>
                </div>
                <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-emerald-600 transition-colors duration-200" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
